/**
 * Ввод на сенсорном экране.
 *
 * `window.prompt` на планшете — беда: в режиме киоска браузер его часто
 * блокирует вовсе, а где показывает, там крошечное поле в системном
 * стиле, без цифровой клавиатуры и с кнопками под палец ребёнка.
 * Кассир в час пик в него не попадает.
 *
 * Здесь два диалога на замену: для слов и для сумм. Оба крупные,
 * оба с правильной клавиатурой, оба закрываются по Esc и по касанию
 * мимо — как остальные окна кассы.
 */
import { useEffect, useState } from 'react';
import { formatMoney } from '@dastarhan/core';
import { useKeyboard } from './useKeyboard';

/** Ввод текста: заметка кухне, комментарий, промокод. */
export function TextPrompt({
  title, hint, initial = '', placeholder, maxLength = 200,
  allowEmpty = false, onDone, onCancel,
}: {
  title: string;
  hint?: string;
  initial?: string;
  placeholder?: string;
  maxLength?: number;
  /** пустая строка — осмысленный ответ (например «убрать заметку») */
  allowEmpty?: boolean;
  onDone: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  // Клавиатура одна на все окна: копировать её разметку в двадцать
  // мест значит развести их в первый же месяц
  const kb = useKeyboard(value, setValue, 'text');

  // Курсор сразу: клавиатура поднимается вместе с окном, и человеку
  // не приходится целиться в поле пальцем
  useEffect(() => { kb.props.ref.current?.focus(); }, []);

  const ok = allowEmpty || value.trim().length > 0;

  return (
    <div className="dialog-backdrop with-kb" role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}>
      <div className="dialog prompt-card" onClick={(e) => e.stopPropagation()}>
        <span className="eyebrow">Ввод текста</span>
        <h1>{title}</h1>
        {hint && <p className="hint">{hint}</p>}

        <input
          {...kb.props}
          className="prompt-field"
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && ok) onDone(value.trim());
            if (e.key === 'Escape') onCancel();
          }}
        />

        <div className="dialog-actions">
          <button className="btn ghost" onClick={onCancel}>Отмена</button>
          <button className="btn accent" disabled={!ok} onClick={() => onDone(value.trim())}>
            Сохранить
          </button>
        </div>
      </div>
      {kb.node}
    </div>
  );
}

/**
 * Ввод суммы: своя клавиатура крупными кнопками.
 *
 * Системную не зовём: на разных планшетах она открывается по-разному,
 * иногда закрывает половину экрана, иногда не открывается вовсе.
 * Своя всегда одинаковая и всегда на месте.
 */
export function MoneyPrompt({
  title, hint, initial = 0, onDone, onCancel,
}: {
  title: string;
  hint?: string;
  /** в тиынах, как везде в системе */
  initial?: number;
  onDone: (amount: number) => void;
  onCancel: () => void;
}) {
  // Держим строкой: «1500» набирается цифра за цифрой, и превращать
  // её в число на каждом нажатии значит терять ведущие нули и точку
  const [raw, setRaw] = useState(initial > 0 ? String(Math.round(initial / 100)) : '');

  const tenge = Number(raw || '0');
  const amount = Math.round(tenge * 100);

  const tap = (d: string) => {
    if (raw.length >= 9) return;
    if (d === '0' && raw === '') return;
    setRaw(raw + d);
  };

  return (
    <div className="dialog-backdrop with-kb" role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}>
      <div className="dialog money-card" onClick={(e) => e.stopPropagation()}>
        <span className="eyebrow">Ввод суммы</span>
        <h1>{title}</h1>
        {hint && <p className="hint">{hint}</p>}

        <div className="money-view">{formatMoney(amount)}</div>

        <div className="money-pad">
          {['1','2','3','4','5','6','7','8','9'].map((d) => (
            <button key={d} onClick={() => tap(d)}>{d}</button>
          ))}
          <button onClick={() => setRaw((v) => v + '00')} className="ghost">00</button>
          <button onClick={() => tap('0')}>0</button>
          <button className="ghost" onClick={() => setRaw((v) => v.slice(0, -1))}>←</button>
        </div>

        <div className="dialog-actions">
          <button className="btn ghost" onClick={onCancel}>Отмена</button>
          <button className="btn accent" disabled={amount <= 0}
            onClick={() => onDone(amount)}>
            Готово
          </button>
        </div>
      </div>
    </div>
  );
}
